/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScheduleTaskType, ScheduleType, TaskRelationType } from '@gh/builder-shared-data';
import { EnumDictionary, getRouteInitData, getRouteStaticData, MutablePageState } from '@gh/common-shared';
import { DialogService, FormMode } from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { currentNormalizedDate } from '@gh/core-util';
import { first$ } from '@gh/rx/operators';
import { CalendarListItem, GanttChartComponent, GanttTask } from '@gh/builder-shared-gantt-chart';
import { ScheduleCombinedGanttDetails } from '../../resources/schedule-combined-gantt-details.model';
import { FilterNames } from '@gh/builder-shared';
import { ScheduleGanttDetailsBusinessService } from '../../resources/schedule-gantt-details-business.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'gh-schedule-combined-gantt-details',
  template: require('./schedule-combined-gantt-details.component.html'),
})
export class ScheduleCombinedGanttDetailsComponent implements OnInit {
  @Input() currentDate = currentNormalizedDate();

  gridLayout: GridLayout;
  taskTypeDict: EnumDictionary<ScheduleTaskType>;
  relationshipTypeDict: EnumDictionary<TaskRelationType>;

  @ViewChild(GanttChartComponent, { static: false }) gantt: GanttChartComponent;
  name: string;
  form: MutablePageState<ScheduleCombinedGanttDetails>;
  calendar: CalendarListItem[];
  initialRatio: number;
  nextTaskNumber: number;
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;
  private scheduleType: ScheduleType = getRouteStaticData(this.route)['type'];

  constructor(private route: ActivatedRoute,
              private ngZone: NgZone,
              private business: ScheduleGanttDetailsBusinessService,
              private dialogs: DialogService) {
  }

  get helpId(): string {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return 'schedules/constructions/combined-gantt-chart';
      case ScheduleType.PreConstruction:
        return 'schedules/pre-constructions/combined-gantt-chart';
      case ScheduleType.Maintenance:
        return 'schedules/warranty/combined-gantt-chart';
      case ScheduleType.Sales:
        return 'schedules/sales/combined-gantt-chart';
      default:
        return '';
    }
  }

  get typeTitle(): string {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        return 'lbl_construction_schedules';
      case ScheduleType.PreConstruction:
        return 'lbl_pre_construction_schedules';
      case ScheduleType.Maintenance:
        return 'lbl_maintenance_schedules';
      case ScheduleType.Sales:
        return 'lbl_sales_schedules';
      default:
        return '';
    }
  }

  get tasks(): GanttTask[] {
    return this.form.value.tasks;
  }

  ngOnInit(): void {
    const { calendar, details, gridLayout, taskTypeDict, relationshipTypeDict, filter } = getRouteInitData(this.route);
    this.initialRatio = filter?.primary || 1;
    this.name = details.name;
    this.taskTypeDict = taskTypeDict;
    this.relationshipTypeDict = relationshipTypeDict;
    this.gridLayout = gridLayout;
    this.calendar = calendar.dates;

    this.form = new MutablePageState(FormMode.EditMode, {
      ...details,
      tasks: details.tasks.filter((task: GanttTask) => !task.markedAsDeleted),
    });

    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.dialogs.warning('msg_combined_gantt_chart_changes');
    });
    this.setHistoryObservable();
  }

  setHistoryObservable(): void {
    if (!this.gantt || !this.gantt.api) {
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => this.setHistoryObservable(), 100);
      return;
    }
    this.gantt.api.initHistory();
    this.isUndoDisabled$ = this.gantt.api.isUndoDisabled$;
    this.isRedoDisabled$ = this.gantt.api.isRedoDisabled$;
  }

  onSizeChange(size: any): void {
    this.business.saveFilter(FilterNames.ganttCombined, size);
  }

  undo(): void {
    this.gantt.api.undo();
  }

  redo(): void {
    this.gantt.api.redo();
  }

  onShowCompletedTasksChange(show: boolean): void {
    this.gantt.api.showCompletedTasks(show);
  }
}
